import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { StateService } from '@app/service/state/state.service';
import Translations from './fi-loan-eligibility-questionnaire-review.component.json';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';

import {
  KalpAnswersFI,
  KalpInfoReason,
  LeqService,
  MartialStatus,
  Occupation,
  TypeOfAccommodation,
} from '@app/service/leq/leq.service';
import { FiRoute } from '@assets/val/route-constants';

@Component({
  selector: 'fi-loan-eligibility-questionnaire-review',
  templateUrl: './fi-loan-eligibility-questionnaire-review.component.html',
  styleUrls: [
    '../../page-shared.css',
    './fi-loan-eligibility-questionnaire-review.component.scss',
  ],
})
export class FiLeqReviewComponent extends RouteComponent implements OnInit {
  loading: boolean = true;
  error: boolean = false;
  navigationInProgress: boolean;

  translations: { [key: string]: string };

  kalpAnswers: KalpAnswersFI;
  currencySymbol: string = ' €';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private fiUserService: FiUserService,
    private leqService: LeqService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];

    await this.setup();
  }

  async setup(): Promise<void> {
    this.kalpAnswers = this.leqService.getKalpAnswersFI();

    if (!this.kalpAnswers) {
      try {
        this.fiUserService
          .getKalpInfo(KalpInfoReason.REJECTED)
          .subscribe((resp) => {
            if (resp) {
              this.kalpAnswers = resp;
              this.leqService.setKalpAnswersFI(resp);
            } else {
              this.error = true;
            }
          });
      } catch (err) {
        this.error = true;
        this.handleError(err);
      }
    }

    this.loading = false;
  }

  getOccupationOptions(occupation: Occupation): string {
    switch (occupation) {
      case Occupation.FULL_TIME_EMPLOYMENT:
        return this.translations.occupationFullTime;
      case Occupation.PART_TIME_EMPLOYMENT:
        return this.translations.occupationPartTime;
      case Occupation.FIX_TERM_EMPLOYMENT:
        return this.translations.occupationFixedTerm;
      case Occupation.SELF_EMPLOYED:
        return this.translations.occupationSelfemployed;
      case Occupation.STUDENT:
        return this.translations.occupationStudent;
      case Occupation.RETIRED:
        return this.translations.occupationRetired;
      case Occupation.UNEMPLOYED:
        return this.translations.occupationUnemployed;
    }
  }

  getMartialStatusOptions(martialStatus: MartialStatus): string {
    switch (martialStatus) {
      case MartialStatus.SINGLE:
        return this.translations.martialStatusSingle;
      case MartialStatus.MARRIED:
        return this.translations.martialStatusMarried;
      case MartialStatus.COHABITANT:
        return this.translations.martialStatusCohabitant;
    }
  }

  getTypeOfAccommodationOtions(
    typeOfAccommodation: TypeOfAccommodation
  ): string {
    switch (typeOfAccommodation) {
      case TypeOfAccommodation.RENT:
        return this.translations.typeOfAccommodationRental;
      case TypeOfAccommodation.OWNER:
        return this.translations.typeOfAccommodationOwner;
      case TypeOfAccommodation.PARTNER_OWNS_PROPERTY:
        return this.translations.typeOfAccommodationPartnerOwned;
      case TypeOfAccommodation.OTHER:
        return this.translations.typeOfAccommodationOther;
    }
  }

  async handleCancellation(): Promise<void> {
    this.navigationInProgress = true;
    this.router.navigate([FiRoute.HOME], {
      queryParamsHandling: 'merge',
    });
    this.navigationInProgress = false;
  }

  async handleCtaClick(): Promise<void> {
    this.loading = true;

    try {
      await this.fiUserService
        .postKalpAnswers(this.kalpAnswers)
        .then(() => this.navigate());
    } catch (err) {
      this.error = true;
      this.handleError(err);
    }

    this.loading = false;
  }

  handleUpdateClick(): void {
    this.navigate();
  }
}
